import { useContext } from 'react';
import { ActiveDatabaseContext } from '../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { KodplanContext } from '../../../../../../Components/KodplanManager/Context/KodplanContext';
import { TEDEKBokforderMinBokforderStatusEnum } from '../../../../../../globalQueryTypes';
import { ModelContext } from '../../../../../../Model/ModelContext/ModelContext';
import { TSelectedDetail } from '../../../../../../Types/MasterDetailTypes';
import {
  IValidateFn,
  IFieldValidations,
  IFormData,
} from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import KonteringContext from '../../../../_Common/Components/KonteringManager/KonteringContext';
import { IKodstrang } from '../../../../_Common/Components/KonteringManager/KonteringManager';
import { BokforderWrapperContext } from '../../../Rutiner/Bokforder/Contexts/BokforderWrapperContext';
import { IUserBokforderKodbehorigheter } from '../Components/BokforderContext/BokforderContextManager';
import {
  UtanordningType,
  UtanordningType__Associations_Rader,
} from '../Queries/_QueryTypes/UtanordningType';
import { createUsersWithKodbehBasedOnType } from '../Utils/BfoUtanordningUtils';
import { getKodplanForBokfOrder } from '../Utils/BokforderUtils';
import { validateKontrollattestant } from '../Utils/validationUtils';
import {
  createKodstrang,
  validateInternKonton,
  validateAttestantMandatory,
  validateBaskontoMandatory,
  validateKombinationErrors,
  validateKombinationVillkorErrors,
  validateAttestantKodbeh,
} from './useValidateBokforder';
import { roundToTwoDecimals } from '../../../../Leverantor/ObjektVyer/FakturaDel/Components/Kontering/Utils/KonteringUtils';
import { FranvaroContext } from '../../../../SystemAdministration/ObjektVyer/User/Context/FranvaroContext/FranvaroContext';
import { filterValidKontrollattestantOption } from '../Utils/UtanordningUtils';

const validateRowSumBelopp = (
  rows: UtanordningType__Associations_Rader[],
  belopp: number | null,
  touched: IFormData,
  minBokforderStatus: TEDEKBokforderMinBokforderStatusEnum | null
): IFieldValidations => {
  const rowSumBelopp = roundToTwoDecimals(
    rows.reduce((sum, row) => {
      if (row.DEBET != null) {
        return (sum += row.DEBET);
      }
      return sum;
    }, 0)
  );

  if (
    ((minBokforderStatus === TEDEKBokforderMinBokforderStatusEnum.UTKAST &&
      touched['U_BELOPP'] != null) ||
      minBokforderStatus !== TEDEKBokforderMinBokforderStatusEnum.UTKAST) &&
    belopp != null &&
    rowSumBelopp !== belopp
  ) {
    const error: IFieldValidations = [
      {
        fieldName: 'U_BELOPP',
        type: 'Error',
        message: 'Beloppet stämmer inte överens med summan av konterade rader',
      },
    ];
    return error;
  }
  return [];
};

const validateTillgodohavandeBelopp = (
  belopp: number | null,
  tillgodohavande: number
): IFieldValidations => {
  if (belopp && belopp > tillgodohavande) {
    const error: IFieldValidations = [
      {
        fieldName: 'U_BELOPP',
        type: 'Error',
        message: 'Beloppet är större än tillgodohavande för befintlig kund',
      },
    ];
    return error;
  }

  return [];
};

export const useValidateUtanordning: (
  selectedDetail: TSelectedDetail
) => IValidateFn<UtanordningType> = () => {
  const { userID } = useContext(ActiveDatabaseContext);
  const { avenyCollectionMap } = useContext(ModelContext);
  const bokforderWrapperContext = useContext(BokforderWrapperContext);
  const franvaroContext = useContext(FranvaroContext);
  const konteringContext = useContext(KonteringContext);
  const kodplaner = useContext(KodplanContext);

  return ({ currentValues, touched }) => {
    let errors: IFieldValidations = [];
    if (
      !konteringContext.konteringValidationQueryLoading &&
      !bokforderWrapperContext.allBFOUsersLoading
    ) {
      const rows = currentValues?._Associations?.Rader;
      const rowsTouched: { [id: string]: IFormData } | undefined =
        touched?._Associations?.Rader;

      if (rows == null) {
        return errors;
      }

      const isBestallare =
        currentValues?._Associations?.Bestallare?.id === userID ||
        currentValues?.STATUS == null;

      const bokforderDate: Date = currentValues?.BOKF_DATUM
        ? new Date(currentValues?.BOKF_DATUM)
        : new Date();

      const bokforderKodplan = getKodplanForBokfOrder(
        kodplaner.availableKodplanList,
        bokforderDate
      );

      const haveKontrollattesterat =
        currentValues.KONTROLLATTESTERAD_DATUM != null;

      const enhancedBokforderKodplan =
        konteringContext.konteringUtils.getKontoPlanById(
          bokforderKodplan?.id ?? ''
        );

      const allUtanordningUsersWithKodbeh: IUserBokforderKodbehorigheter[] =
        createUsersWithKodbehBasedOnType(
          bokforderWrapperContext.allBFOUsers,
          avenyCollectionMap,
          bokforderDate,
          'FOR_UTANORDNING',
          userID ?? '',
          bokforderWrapperContext.isEconomyPersonnel
        );

      const allKontrollattestanterWithKodbeh: IUserBokforderKodbehorigheter[] =
        createUsersWithKodbehBasedOnType(
          bokforderWrapperContext.allBFOUsers,
          avenyCollectionMap,
          bokforderDate,
          'UTANORDNING_KONTROLL',
          userID ?? '',
          bokforderWrapperContext.isEconomyPersonnel
        );

      const currentKontrollattestantKodbeh =
        allKontrollattestanterWithKodbeh.find(
          ka =>
            ka.userID === currentValues?._Associations?.Kontrollattestant?.id
        );

      const kodstrangar: IKodstrang[] = rows.reduce<IKodstrang[]>((prev, row) => {
        const potentialKodstrang = createKodstrang(
          row,
          enhancedBokforderKodplan?.kontodelDefs ?? []
        );

        if (potentialKodstrang !== null) {
          return [...prev, potentialKodstrang];
        } else {
          return prev;
        }
      }, []);

      // Validera så att användaren inte blandar inter och extern konton
      errors = errors.concat(
        validateInternKonton(rows, enhancedBokforderKodplan?.kontodelDefs ?? [])
      );

      // Obligatoriskt med attestant på rad
      errors = errors.concat(validateAttestantMandatory(rows, rowsTouched));

      // Obligatoriskt med ett värde på baskonto
      errors = errors.concat(validateBaskontoMandatory(rows, rowsTouched));

      if (enhancedBokforderKodplan && kodstrangar.length > 0) {
        // Validera kombination i kodsträngen
        errors = errors.concat(
          validateKombinationErrors(
            kodstrangar,
            enhancedBokforderKodplan,
            bokforderDate,
            konteringContext.konteringUtils,
            rowsTouched
          )
        );
        // Validera kombinationvillkor i kodsträngen
        errors = errors.concat(
          validateKombinationVillkorErrors(
            kodstrangar,
            enhancedBokforderKodplan,
            bokforderDate,
            konteringContext.konteringUtils,
            rowsTouched
          )
        );

        errors = errors.concat(
          validateAttestantKodbeh(
            rows,
            rowsTouched,
            allUtanordningUsersWithKodbeh,
            kodstrangar
          )
        );
      }

      if (!haveKontrollattesterat) {
        const validKontrollattestanter =
          allKontrollattestanterWithKodbeh.filter(kontrollattestant =>
            filterValidKontrollattestantOption(
              currentValues?._Associations?.Bestallare?.id,
              kontrollattestant,
              kodstrangar,
              franvaroContext.franvaroReservForData,
              franvaroContext.franvaroReservForQueryLoading,
              userID,
              currentValues?.U_BELOPP,
              rows,
              currentValues?.STATUS
            )
          );
        errors = errors.concat(
          validateKontrollattestant(
            rows,
            touched,
            currentKontrollattestantKodbeh,
            isBestallare,
            currentValues?.U_BELOPP,
            allUtanordningUsersWithKodbeh,
            kodstrangar,
            userID,
            currentValues?._Associations?.Bestallare?.id,
            franvaroContext.franvaroReservForData,
            validKontrollattestanter ?? []
          )
        );
      }

      //Validera om det finns en diff mellan summan av beloppet på raderna och det som står i detaljkortet
      errors = errors.concat(
        validateRowSumBelopp(
          rows,
          currentValues?.U_BELOPP,
          touched,
          currentValues.MinBokforderStatus
        )
      );

      const tillgodohavande =
        currentValues?._Associations?.Kund?.Tillgodohavande;
      if (tillgodohavande > 0 && currentValues.U_BELOPP > 0) {
        errors = errors.concat(
          validateTillgodohavandeBelopp(currentValues.U_BELOPP, tillgodohavande)
        );
      }
    }

    return errors;
  };
};
